import {RouterPrefix} from '@wix/wixstores-client-core';
import {SiteStore} from '@wix/wixstores-client-storefront-sdk/dist/es/src/viewer-script/site-store/SiteStore';
import {ReadOnlyExperiments} from '@wix/wix-experiments';
import _ from 'lodash';
import {Experiments, STORES_CATEGORY_SEO, STORES_GALLERY_SEO, STORES_SUB_CATEGORY_SEO} from '../constants';
import {GalleryQueryParamsService} from './GalleryQueryParamsService';
import {ProductsService} from './ProductsService';
import {CategoriesService} from './CategoriesService';
import {ICategory} from '../types/category';
import {IProduct} from '../types/galleryTypes';

export interface GallerySEOServiceDeps {
  siteStore: SiteStore;
  queryParamsService: GalleryQueryParamsService;
  productsService: ProductsService;
  categoriesService: CategoriesService;
  experiments: ReadOnlyExperiments;
}

interface SEORenderParams {
  currentPage: number;
  totalPages: number;
  maxProductsPerPage: number;
  getProductPageUrl: (slug: string) => string;
}

interface CategorySEORenderParams extends SEORenderParams {
  currentCategory: ICategory;
  translations: Record<string, string>;
}

type GallerySEORenderParams = SEORenderParams;

export class GallerySEOService {
  private readonly siteStore: SiteStore;
  private readonly queryParamsService: GalleryQueryParamsService;
  private readonly productsService: ProductsService;
  private readonly categoriesService: CategoriesService;
  private readonly experiments: ReadOnlyExperiments;

  constructor(deps: GallerySEOServiceDeps) {
    this.siteStore = deps.siteStore;
    this.queryParamsService = deps.queryParamsService;
    this.productsService = deps.productsService;
    this.categoriesService = deps.categoriesService;
    this.experiments = deps.experiments;
  }

  public getDataForSEOCarousel(shouldSendSubcategoriesSeoData: boolean, getProductPageUrl: (slug: string) => string) {
    if (!shouldSendSubcategoriesSeoData) {
      return [];
    }
    return this.productsService.products?.slice(0, 5).map((product: IProduct, index: number) => {
      return {
        position: index + 1,
        name: product.name,
        price: product.price,
        url: getProductPageUrl(product.urlPart),
        image: product.media[0]?.fullUrl,
        currency: this.siteStore.currency,
      };
    });
  }

  public nextPrevLinksMapped(currentPage: number, totalPages: number): {prevUrl: string; nextUrl: string} {
    const res = {prevUrl: '', nextUrl: ''};
    const prevIndex = currentPage - 1;
    const nextIndex = currentPage + 1;

    if (prevIndex > 0) {
      res.prevUrl = this.queryParamsService.getUrlWithCustomPageParamForSeo(prevIndex);
    }

    if (nextIndex <= totalPages) {
      res.nextUrl = this.queryParamsService.getUrlWithCustomPageParamForSeo(nextIndex);
    }

    return res;
  }

  public nextPrevLinks(currentPage: number, totalPages: number): string[] {
    const {prevUrl, nextUrl} = this.nextPrevLinksMapped(currentPage, totalPages);
    return [prevUrl, nextUrl].filter((url) => url);
  }

  public getLinksForAllPages(totalPages: number): string[] {
    /* istanbul ignore next: edge case cap - impractical to test with 1000+ pages */
    const numberOfPages = totalPages < 1000 ? totalPages : 1000;
    return _.times(numberOfPages, (i) => this.queryParamsService.getUrlWithCustomPageParamForSeo(i + 1));
  }

  public setAppropriateSEOPageCode(currentPage: number, maxProductsPerPage: number) {
    const totalProducts = this.productsService.totalCount;
    const startIndex = (currentPage - 1) * maxProductsPerPage;
    const shouldSetSEOStatus404 = startIndex > totalProducts && totalProducts > 0;
    if (shouldSetSEOStatus404) {
      this.siteStore.seo.setSeoStatusCode(404);
    }
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  public renderCategorySEOTags(params: CategorySEORenderParams) {
    const {currentPage, totalPages, getProductPageUrl, currentCategory, translations} = params;
    const {prevUrl, nextUrl} = this.nextPrevLinksMapped(currentPage, totalPages);
    const shouldSendSubcategoriesSeoData = this.experiments.enabled(Experiments.SendSubcategoriesSeoDataSF);

    const categoryUrl = this.queryParamsService.getUrlWithCustomPageParamForSeo(currentPage);
    const itemType =
      currentCategory.parentCategoryId && shouldSendSubcategoriesSeoData
        ? STORES_SUB_CATEGORY_SEO
        : STORES_CATEGORY_SEO;

    const categorySeoData = this.categoriesService.getCategorySeoData(
      currentCategory.id,
      currentCategory.parentCategoryId,
      currentCategory.breadcrumbs
    );

    const seoBreadcrumbs: {position: number; name: string; item: string}[] = [
      {position: 1, name: translations['gallery.breadcrumbs.firstItem'], item: this.siteStore.location.baseUrl},
    ];
    categorySeoData.breadcrumbs && seoBreadcrumbs.push(...categorySeoData.breadcrumbs);
    seoBreadcrumbs.push({
      position: seoBreadcrumbs.length + 1,
      name: currentCategory.name,
      item: `${this.siteStore.location.baseUrl}/${RouterPrefix.CATEGORY}/${currentCategory.slug}`,
    });

    const products = this.getDataForSEOCarousel(shouldSendSubcategoriesSeoData, getProductPageUrl);

    const breadcrumbsInfo = shouldSendSubcategoriesSeoData
      ? {
          breadcrumbs:
            currentCategory.breadcrumbs?.map((breadcrumb) => ({
              categoryId: breadcrumb.id,
              categoryName: breadcrumb.name,
              categorySlug: breadcrumb.slug,
            })) ?? [],
        }
      : undefined;

    const category = shouldSendSubcategoriesSeoData
      ? {
          ...currentCategory,
          categoryUrl,
          image: currentCategory.media
            ? {
                url: currentCategory.media.fullUrl,
                width: currentCategory.media.width,
                height: currentCategory.media.height,
              }
            : undefined,
          itemCounter: currentCategory.numOfProducts,
          parentCategory: currentCategory.parentCategoryId
            ? {
                id: currentCategory.parentCategoryId,
                index: currentCategory.parentCategoryIndex,
              }
            : undefined,
          breadcrumbsInfo,
          pagination: {
            prevUrl,
            nextUrl,
            totalPages,
            currentPage,
          },
        }
      : {
          categoryUrl,
          categorySlug: currentCategory.slug,
          categoryName: currentCategory.name,
          categoryDesc: currentCategory.description,
          numberOfItems: this.productsService.totalCount,
          image: {
            imageUrl: currentCategory.media?.fullUrl,
            imageWidth: currentCategory.media?.width,
            imageHeight: currentCategory.media?.height,
            hasImage: !!currentCategory.media,
            imageAlt: currentCategory.name,
          },
          pagination: {
            prevUrl,
            nextUrl,
            totalPages,
            currentPage,
          },
        };

    const itemData = {
      products,
      category,
    };
    const seoData = currentCategory.seoData && JSON.parse(currentCategory.seoData);

    this.siteStore.seo.renderSEOTags({
      itemType,
      itemData,
      seoData,
    });
  }

  public renderGallerySEOTags(params: GallerySEORenderParams) {
    const {currentPage, totalPages, getProductPageUrl} = params;
    const shouldSendSubcategoriesSeoData = this.experiments.enabled(Experiments.SendSubcategoriesSeoDataSF);
    const {prevUrl, nextUrl} = this.nextPrevLinksMapped(currentPage, totalPages);
    const url = this.queryParamsService.getUrlWithCustomPageParamForSeo(currentPage);
    const itemType = STORES_GALLERY_SEO;

    const products = this.getDataForSEOCarousel(shouldSendSubcategoriesSeoData, getProductPageUrl);
    const itemData = {
      products,
      shop: {
        url,
      },
      pagination: {
        prevUrl,
        nextUrl,
        totalPages,
        currentPage,
      },
      items: {
        numberOfItems: this.productsService.totalCount,
      },
    };

    this.siteStore.seo.renderSEOTags({
      itemType,
      itemData,
    });
  }
}
